import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useChatStream } from '../hooks/useChatStream';
import { useLibrary } from '../hooks/useLibrary';
import { useSongFlow } from '../hooks/useSongFlow';
/** ⭐ 0017（`decisions/041`）：登入狀態（額度顯示／出歌後更新） */
import { useAuth } from '../hooks/useAuth';
import { usePlayer } from '../player/PlayerContext';
/** ⭐ **UI v2 步 5–7**（`decisions/052`）：UI／邏輯**純搬**出嚟（零行為改動） */
import type { SheetKind } from '../features/chat/types';
import { createMessageRenderers } from '../features/chat/messageActions';
import { useMustUse } from '../features/chat/useMustUse';
import { useChatPrefs } from '../features/chat/useChatPrefs';
import { useSongEdit } from '../features/chat/useSongEdit';
import { useSession } from '../features/chat/useSession';
import { useSongActions } from '../features/chat/useSongActions';
import ChatView, { type ChatController } from '../features/chat/ChatView';

/**
 * 對話畫面（`decisions/025`／`026`：**一切喺 chatbox 內**）
 *
 *   ① 揀語言 → **理解訊息**（〔啱，寫落去〕）
 *   ② **歌詞訊息**（〔出歌〕〔手動編輯〕〔複製〕）—— 手動編輯 ＝ **訊息內逐句高亮 ＋ 輸入**
 *   ③ **歌訊息**（內嵌播放器 ＋ 下面 3 粒掣：分享／換風格／去播放器）
 *   ⭐ Must-use：撳〔原話〕／〔意思〕之後，**喺任何自己嘅訊息逐句撳**（唔限輸入框）
 */

export default function ChatPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const { state, send, retry, sessionId, deviceId, startNewChat, openSession, reload } = useChatStream(i18n.language);
  const flow = useSongFlow({ locale: i18n.language, sessionId, deviceId });
  /** ⭐ 0017：登入狀態（出歌要額度；出完即刻更新顯示） */
  const auth = useAuth();
  const library = useLibrary();
  const player = usePlayer();


  /** ⭐ **UI v2 步 6**（`decisions/052`）：狀態／effects 抽去 `src/features/chat/use*.ts`（零行為改動） */
  const { pinned, setPinned, armed, setArmed } = useMustUse(sessionId);

  /** 彈窗 */
  const [sheet, setSheet] = useState<SheetKind>('none');
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState(false);
  /** ⭐ 出歌輪詢（每 8 秒問狀態；見下面 effect） */
  const [renderingId, setRenderingId] = useState<string | null>(null);
  const [keepGoing, setKeepGoing] = useState(false);

  const {
    songLang, setSongLang, langOptions,
    songRegister, setSongRegister, registerOptions,
    useVoice, setUseVoice, voiceReady,
    vocalGender, setVocalGender, styleOptions,
    renderVariants, setRenderVariants,
    songGenre, genreOptions, genreQuick, pickGenre, toggleQuick,
    mode, setMode,
  } = useChatPrefs({ t, locale: i18n.language, sessionId });

  /** 某首歌嘅歌詞（手動編輯嘅基準） */
  const lyricsOf = (songId: string): string =>
    library.songs.find((s) => s.id === songId)?.lyrics ?? (flow.song?.id === songId ? flow.song.lyrics : '');

  const {
    editSongId, editPicked, editDrafts,
    startEdit, toggleEditLine, setDraft, exitEdit,
    applyWithAi, applyDirect,
    styleSelected, setStyleSelected, styleFree, setStyleFree,
    openStyle, runRemake,
  } = useSongEdit({
    t, sessionId, deviceId, locale: i18n.language, vocalGender, lyricsOf, styleOptions,
    setSheet, setBusyAction, setActionError, setRenderingId, reload,
  });

  const {
    playlistSongId, setPlaylistSongId,
    history, historyBusy, historyBusyId, historyError, setHistoryError,
    renameTarget, setRenameTarget, deleteTarget, setDeleteTarget,
    openHistory, commitRename, commitDelete,
    shareFiles,
  } = useSession({ t, deviceId, sessionId, startNewChat, songs: library.songs, setSheet, setArmed, exitEdit });

  const hasConversation = state.messages.some((m) => m.role === 'user');
  const songFinished = Boolean(flow.song) && ['lyrics_ready', 'running', 'succeeded'].includes(flow.song?.status ?? '');

  /** 出歌中：每 8 秒問狀態；完成／失敗 → reload（`song` 訊息會出現） */
  useEffect(() => {
    if (!renderingId) return;
    let alive = true;
    const timer = setInterval(async () => {
      try {
        const res = await fetch(`/api/song/status/${renderingId}`);
        const data = (await res.json().catch(() => ({}))) as { song?: { status?: string } };
        if (!alive) return;
        const st = data.song?.status ?? '';
        if (st === 'succeeded' || st === 'failed') {
          clearInterval(timer);
          setRenderingId(null);
          reload();
        }
      } catch {
        /* 下一輪再試 */
      }
    }, 8000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [renderingId, reload]);
  /** ⭐ **UI v2 步 7**（`decisions/052`）：出歌流程／播放動作抽去 `features/chat/useSongActions.ts` */
  const { handleSend, openStartSong, runUnderstand, runWriteLyrics, runRender, playSong, audioUrlForShare } = useSongActions({
    t, sessionId, deviceId, locale: i18n.language, flow, auth, library, player, send, pinned,
    songLang, songRegister, songGenre, voiceReady, useVoice, renderVariants, vocalGender, mode,
    input, setInput, songFinished, keepGoing, setKeepGoing,
    setSheet, setBusyAction, setActionError, setRenderingId, reload,
  });


  const langBtn = (active: boolean) => (active ? 'lang-btn lang-btn--active' : 'lang-btn');
  /**
   * ⭐ **UI v2 步 5**（`decisions/052`）：`renderExtra`／`messageActions` 已搬去
   *   `features/chat/messageActions.tsx`（純搬；依賴以 `ctx` 傳入）。
   */
  const { renderExtra, messageActions } = createMessageRenderers({
    t, navigate, songs: library.songs, player, playSong,
    editSongId, editPicked, editDrafts, busyAction, toggleEditLine, setDraft, lyricsOf,
    renderVariants, setRenderVariants, vocalGender, setVocalGender, voiceReady, useVoice, setUseVoice,
    runRender, runWriteLyrics, setArmed,
    startEdit, applyWithAi, applyDirect, exitEdit, renderingId,
    openStyle, setPlaylistSongId, shareFiles, audioUrlForShare, langBtn,
  });

  /** ⭐ **UI v2 步 7**（`decisions/052`）：視圖（JSX）搬去 `features/chat/ChatView.tsx`（零行為改動） */
  const c: ChatController = {
    t, state, retry, flow, deviceId, sessionId, openSession, startNewChat,
    onLibraryReload: () => void library.reload(),
    pinned, setPinned, armed, setArmed,
    input, setInput, handleSend, openStartSong,
    mode, setMode,
    songLang, setSongLang, langOptions,
    songRegister, setSongRegister, registerOptions,
    songGenre, pickGenre, genreOptions, genreQuick, toggleQuick,
    styleOptions, styleSelected, setStyleSelected, styleFree, setStyleFree,
    sheet, setSheet, runUnderstand, runRemake,
    history, historyBusy, historyBusyId, historyError, setHistoryError,
    renameTarget, setRenameTarget, deleteTarget, setDeleteTarget,
    openHistory, commitRename, commitDelete, playlistSongId, setPlaylistSongId,
    busyAction, renderingId, actionError, songFinished, keepGoing, setKeepGoing, hasConversation,
    exitEdit, playSong, langBtn, renderExtra, messageActions,
  };

  return <ChatView c={c} />;
}
